import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import TranslateIcon from '@mui/icons-material/Translate';
import GroupsIcon from '@mui/icons-material/Groups';
import SecurityIcon from '@mui/icons-material/Security';
import { useAuthStore } from '../contexts/authStore';

export default function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [meetingCode, setMeetingCode] = useState('');

  const handleJoinMeeting = () => {
    if (meetingCode.trim()) {
      navigate(`/${meetingCode.trim()}`);
    }
  };

  const features = [
    {
      icon: <VideoCallIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'HD Video Calls',
      description: 'Crystal clear video quality with adaptive bitrate for the best experience.',
    },
    {
      icon: <TranslateIcon sx={{ fontSize: 48, color: 'secondary.main' }} />,
      title: 'Real-time Translation',
      description: 'AI-powered live translation supporting multiple languages with natural voices.',
    },
    {
      icon: <GroupsIcon sx={{ fontSize: 48, color: 'success.main' }} />,
      title: 'Multi-party Meetings',
      description: 'Host meetings with multiple participants with easy screen sharing.',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 48, color: 'warning.main' }} />,
      title: 'Secure & Private',
      description: 'End-to-end encryption with waiting room and host controls.',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(180deg, rgba(6,147,227,0.1) 0%, rgba(155,81,224,0.05) 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  background: 'linear-gradient(135deg, #ffffff 0%, #a0a0b0 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Video Meetings with Real-time Translation
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                Connect with anyone, anywhere. Break language barriers with AI-powered
                live translation during your video calls.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                {isAuthenticated ? (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/dashboard')}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    Start a Meeting
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/register')}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    Get Started Free
                  </Button>
                )}
              </Box>

              {/* Join meeting input */}
              <Box sx={{ display: 'flex', gap: 2, maxWidth: 400 }}>
                <TextField
                  fullWidth
                  placeholder="Enter meeting code"
                  value={meetingCode}
                  onChange={(e) => setMeetingCode(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleJoinMeeting()}
                  size="small"
                />
                <Button
                  variant="outlined"
                  onClick={handleJoinMeeting}
                  disabled={!meetingCode.trim()}
                >
                  Join
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  p: 3,
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                }}
              >
                {/* Mock video grid */}
                <Grid container spacing={2}>
                  {[1, 2, 3, 4].map((i) => (
                    <Grid item xs={6} key={i}>
                      <Box
                        sx={{
                          aspectRatio: '16/9',
                          bgcolor: 'surface.main',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="h4" color="text.secondary">
                          {['JD', 'MK', 'AS', 'LC'][i - 1]}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {/* Mock controls */}
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  {['Mic', 'Video', 'Share', 'Chat'].map((label) => (
                    <Box
                      key={label}
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: label === 'Mic' ? 'error.main' : 'surface.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h3" align="center" sx={{ mb: 2, fontWeight: 600 }}>
          Everything you need for effective meetings
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Powerful features to make your video calls productive and accessible
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
